import React from "react"
import type { ReactNode } from "react"
import { ColorPicker as ColorPickerPrimitive } from "react-aria-components/ColorPicker"
import { cn } from "../utils"
import { useColorValue } from "./use-color-value"

const panelStyles = [
  "flex w-[12.5rem] flex-col p-3",
  "[&>[data-slot=color-panel-hex]]:mb-2",
  // Actions after the picker; the swatch sections bring their own spacing
  "[&>[data-slot=color-panel-picker]~*:not([data-slot$=-colors])]:mt-3"
]

export type ColorPanelProps = {
  /** The color: uppercase `#RRGGBB`, or "transparent" */
  value: string
  /** Called with the new color as uppercase `#RRGGBB`, or "transparent" */
  onValueChange?: (value: string) => void
  /**
   * `ColorPanel.Hex`, `ColorPanel.Picker`, the swatch sections it needs
   * (`ColorPanel.SavedColors`, `ColorPanel.DesignColors`), and any actions
   */
  children?: ReactNode
  /** Additional CSS classes for layout */
  className?: string
}

/**
 * The picker without a field or popover, for surfaces that already float it,
 * such as a `Menu` popup in the editor toolbar. Holds the color for the parts
 * inside it.
 *
 * @example
 * <ColorPanel value={color} onValueChange={setColor}>
 *   <ColorPanel.Hex />
 *   <ColorPanel.Picker transparency />
 *   <ColorPanel.SavedColors />
 *   <ColorPanel.DesignColors />
 * </ColorPanel>
 */
export function ColorPanelRoot({
  value,
  onValueChange,
  children,
  className
}: ColorPanelProps) {
  const { color, handleChange } = useColorValue(value, onValueChange)

  return (
    <ColorPickerPrimitive value={color} onChange={handleChange}>
      <div data-slot="color-panel" className={cn(panelStyles, className)}>
        {children}
      </div>
    </ColorPickerPrimitive>
  )
}

ColorPanelRoot.displayName = "ColorPanel"
